import forEach from 'lodash/forEach';
import size from 'lodash/size';
import times from 'lodash/times';
import moment from 'moment';
import axios from 'axios';
import {APIRegistry} from '@spothero/utils/api';
import SegmentUtils from 'utils/segment';
import SHGlobals from 'sh-globals';
import SearchTracking from 'search-tracking';
import SearchUtils from 'search-utils';
import StorageUtils from 'storage-utils';

export const GET_SPOTS_RADIAL = 'GET_SPOTS_RADIAL';
export const GET_SPOTS_TILED = 'GET_SPOTS_TILED';
export const RADIAL_SPOTS_INCREMENT = 'RADIAL_SPOTS_INCREMENT';
export const TILED_SPOTS_INCREMENT = 'TILED_SPOTS_INCREMENT';
export const CANCEL_SPOT_REQUESTS = 'CANCEL_SPOT_REQUESTS';
export const CLEAR_SPOTS_DATA = 'CLEAR_SPOTS_DATA';

const SEARCH_UUID_COOKIE = 'sh-search-uuid';
const cancelSources = [];
const cancelCurrentRequests = () => {
    while (cancelSources.length) {
        cancelSources[0].cancel();
        cancelSources.shift();
    }
};
const checkForErrorsInResponse = args => {
    const errorData = [];

    forEach(args, responseData => {
        if (responseData.status && responseData.status >= 400) {
            const errors = responseData.data.data.errors;

            if (errors) {
                errorData.push(...errors);
            }
        }
    });

    return errorData;
};
const trackSegment = (state) => {
    const {
        params: {
            monthly,
            search_string: searchQuery,
            latitude,
            longitude
        },
        geolocated,
        destinationTitle
    } = state;

    const searchUUIDCookie = StorageUtils.get(SEARCH_UUID_COOKIE, 'cookie');
    const {
        sessionUUID,
        searchUUID,
        action
    } = SearchTracking.getValues();
    const searchEventProps = {
        'search query': searchQuery || destinationTitle,
        'session id': sessionUUID,
        'parking type': (monthly) ? 'monthly' : 'transient',
        'search type': action,
        'search source': 'widget'
    };

    if (geolocated) {
        searchEventProps['user location'] = `(${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
    }

    if (searchUUIDCookie !== searchUUID) {
        SegmentUtils.track({
            event: 'Products Searched',
            properties: searchEventProps
        });
    }
};

export const cancelSpotRequests = () => {
    cancelCurrentRequests();

    return {
        type: CANCEL_SPOT_REQUESTS
    };
};

export const getSpotsRadial = ({dateTimeChanged = false, autoZoom = false}) => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const prevSearchState = getState().search.data;
        const mapBounds = prevSearchState.searchMapInstance.getBounds();
        const searchState = SearchUtils.prepareRequest(
            'radial',
            mapBounds,
            prevSearchState
        );
        const {
            requestIncrements,
            radialDistances,
            isDefaultTimes,
            geolocated
        } = searchState;
        const eventId = searchState.params.eid;
        const requests = [];
        const searchAPIUtils = APIRegistry.get('ratesSearch');
        let trackingAction = (eventId) ? 'EVENT' : 'SEARCH';
        let url = 'facilities/rates/';

        if (eventId) {
            url = `events/${eventId}/rates/`;
        }

        if (geolocated) {
            trackingAction = 'FIND_ME';
        }

        if (dateTimeChanged) {
            trackingAction = 'DATE_CHANGED';
        }

        if (autoZoom) {
            trackingAction = 'AUTO_ZOOM';
        }

        SearchTracking.updateAction(trackingAction);

        trackSegment(searchState);

        const params = SearchTracking.assignParams({
            params: searchState.params,
            isDefaultTimes,
            searchText: searchState.params.search_string || searchState.destinationTitle
        });

        if (params.monthly) {
            params.monthly_start_date = moment(params.monthly_start_date).format(SHGlobals.URL_DATE_FORMAT);

            url += "monthly/";
            delete params.monthly;

            delete params.starts;
            delete params.ends;
        } else {
            delete params.monthly_start_date;
        }

        times(requestIncrements, () => {
            const data = {
                ...params,
                include: `${params.include},destination.places_place_ids`,
                source: 'widget',
                distance__gt: Number(radialDistances[0]),
                distance__lt: Number(radialDistances[1])
            };
            const cancelSource = axios.CancelToken.source();

            radialDistances.shift();

            cancelSources.push(cancelSource);
            requests.push(
                searchAPIUtils
                    .get(url, data, {
                        cancelToken: cancelSource.token
                    })
                    .then(response => {
                        const responseData = response.data.data;
                        const destinations = responseData.destinations;
                        let destination = null;

                        if (destinations && size(destinations[0])) {
                            destination = destinations[0];
                        }

                        dispatch({
                            type: RADIAL_SPOTS_INCREMENT,
                            payload: responseData.results,
                            meta: destination
                        });

                        return responseData.results;
                    })
                    .catch(error => {
                        return error;
                    })
            );
        });

        return dispatch({
            type: GET_SPOTS_RADIAL,
            payload: searchAPIUtils
                .all(requests)
                .then(searchAPIUtils.spread((...args) => {
                    const errors = checkForErrorsInResponse(args);

                    if (errors.length) {
                        return Promise.reject(errors);
                    }
                }))
                .catch(error => {
                    return Promise.reject(error);
                }),
            meta: {
                numRequests: requestIncrements,
                finalParams: params
            }
        });
    };
};

export const getSpotsTiled = bounds => {
    cancelCurrentRequests();

    return (dispatch, getState) => {
        const prevSearchState = getState().search.data;
        const searchState = SearchUtils.prepareRequest(
            'tiled',
            bounds || prevSearchState.searchMapInstance.getBounds(),
            prevSearchState
        );
        const {
            map,
            tiles,
            requestIncrements,
            isDefaultTimes
        } = searchState;
        const eventId = searchState.params.eid;
        const requests = [];
        let url = (eventId)
            ? `events/${eventId}/rates/`
            : 'facilities/rates/';
        const params = SearchTracking.assignParams({
            params: searchState.params,
            isDefaultTimes,
            searchText: searchState.params.search_string || searchState.destinationTitle
        });
        const searchAPIUtils = APIRegistry.get('ratesSearch');

        if (params.monthly) {
            params.monthly_start_date = moment(params.monthly_start_date).format(SHGlobals.URL_DATE_FORMAT);

            url += "monthly/";
            delete params.monthly;

            delete params.starts;
            delete params.ends;
        } else {
            delete params.monthly_start_date;
        }

        while (tiles.length) {
            const data = {
                ...params,
                ...SearchUtils.getTileData(tiles[0], map),
                include: `${params.include},destination.places_place_ids`,
                source: 'widget'
            };
            const cancelSource = axios.CancelToken.source();

            tiles.shift();

            cancelSources.push(cancelSource);
            requests.push(
                searchAPIUtils
                    .get(url, data, {
                        cancelToken: cancelSource.token
                    })
                    .then(response => {
                        const responseData = response.data.data.results;

                        dispatch({
                            type: TILED_SPOTS_INCREMENT,
                            payload: responseData
                        });

                        return responseData;
                    })
                    .catch(error => {
                        return error;
                    })
            );
        }

        return dispatch({
            type: GET_SPOTS_TILED,
            payload: searchAPIUtils
                .all(requests)
                .then(searchAPIUtils.spread((...args) => {
                    const errors = checkForErrorsInResponse(args);

                    if (errors.length) {
                        return Promise.reject(errors);
                    }
                }))
                .catch(error => {
                    return Promise.reject(error);
                }),
            meta: {
                numRequests: requestIncrements,
                finalParams: params
            }
        });
    };
};

export const clearSpotsData = () => {
    return {
        type: CLEAR_SPOTS_DATA
    };
};



// WEBPACK FOOTER //
// ./widget/src/js/common/spots-actions.js